import { usePostHog } from "posthog-js/react";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { AssistantConversationFiltersDto, AssistantPageContextRequest } from "@/api/types";

import { AskNobiChatDrawer } from "./AskNobiChatDrawer";
import { useAskNobiChat } from "./helpers/useAskNobiChat";

export interface AskNobiConfig {
  suggestions?: string[];
  defaultFilters: AssistantConversationFiltersDto;
  /** Page context passed to the AI to help it understand the current page. */
  context: Omit<AssistantPageContextRequest, "filters" | "page" | "entity" | "entityId"> & {
    filters?: Record<string, string | string[] | number[] | number | boolean | undefined | null>;
  } & (
      | {
          page: "list";
          // make sure this is a plural entity for better AI understanding
          entity:
            | "addresses"
            | "alerts"
            | "bookable-assets"
            | "bookings"
            | "chats"
            | "companies"
            | "documents"
            | "events"
            | "help-categories"
            | "integrations"
            | "interest-groups"
            | "community-feed"
            | "service-partners"
            | "surveys"
            | "tickets"
            | "ticket-categories"
            | "ticket-statuses"
            | "users";
          entityId?: string;
        }
      | {
          page: "detail";
          // make sure this is a singular entity for better AI understanding
          entity:
            | "bookable-asset"
            | "documents"
            | "event"
            | "message"
            | "poll"
            | "service-partner"
            | "survey"
            | "ticket"
            | "user";
          entityId: string;
        }
      | {
          entity:
            | "home"
            | "portfolio"
            | "analytics"
            | "feeling-at-home"
            | "analytics adoption"
            | "analytics social contribution"
            | "analytics engagement";
          page: "detail";
          entityId?: never;
        }
    );
  /** If provided, this message is automatically sent when the drawer opens. */
  initialMessage?: string;
}

interface AskNobiContextValue {
  open: (config: AskNobiConfig) => void;
  close: () => void;
  isOpen: boolean;
}

const AskNobiContext = createContext<AskNobiContextValue | null>(null);

export function useAskNobi(): AskNobiContextValue {
  const ctx = useContext(AskNobiContext);
  if (!ctx) {
    throw new Error("useAskNobi must be used inside AskNobiProvider");
  }

  return ctx;
}

export function AskNobiProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const postHog = usePostHog();
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<AskNobiConfig>();
  const chat = useAskNobiChat({});

  const initialMessageSentRef = useRef(false);

  const close = useCallback(() => {
    setIsOpen(false);
    chat.clearChat();
    initialMessageSentRef.current = false;
  }, [chat]);

  const { changeFilters, changeContext } = chat;
  const open = useCallback(
    (nextConfig: AskNobiConfig) => {
      postHog.capture("clicked_ask_nobi");
      setConfig(nextConfig);
      changeFilters(nextConfig.defaultFilters);
      changeContext({
        ...nextConfig?.context,
        filters: formatFiltersToAiText(nextConfig.context.filters),
      });
      initialMessageSentRef.current = false;
      setIsOpen(true);
    },
    [changeContext, changeFilters, postHog],
  );

  const { sendMessage } = chat;
  useEffect(() => {
    if (!isOpen || !config?.initialMessage || initialMessageSentRef.current) {
      return;
    }

    initialMessageSentRef.current = true;
    void sendMessage(config.initialMessage);
  }, [isOpen, config?.initialMessage, sendMessage]);

  const contextValue = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <AskNobiContext value={contextValue}>
      {children}
      <AskNobiChatDrawer isOpen={isOpen} onClose={close} chat={chat} suggestions={config?.suggestions} />
    </AskNobiContext>
  );
}

function formatFiltersToAiText(filters?: AskNobiConfig["context"]["filters"]): string {
  if (!filters) {
    return "";
  }

  return Object.entries(filters)
    .filter(([_, value]) => !!value && !(Array.isArray(value) && value.length === 0))
    .map(([key, value]) => {
      const formattedValue = Array.isArray(value) ? value.join(",") : String(value);

      return `${key}=${formattedValue}`;
    })
    .join("&");
}
